import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Cookie, Settings, X, Shield, BarChart3, Megaphone } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CookieConsentBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState({
    cookies_essential: true, // Always true
    cookies_analytics: false,
    cookies_marketing: false,
    email_marketing: false
  });

  useEffect(() => {
    // Check if consent already given. Runs once on mount — no closure deps so
    // an empty array is correct here.
    const consentId = localStorage.getItem('cookieConsentId');
    if (!consentId) {
      setShowBanner(true);
    }
  }, []);

  const saveConsent = async (updatedConsent) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/gdpr/consent`, updatedConsent);
      localStorage.setItem('cookieConsentId', response.data.consent_id);
      localStorage.setItem('cookieConsent', JSON.stringify(updatedConsent));
      // Load analytics / marketing scripts if the user just opted in.
      if (typeof window.__applyConsent === 'function') {
        window.__applyConsent(updatedConsent);
      }
      setShowBanner(false);
      setShowSettings(false);
    } catch (error) {
      // Still hide banner and save locally
      localStorage.setItem('cookieConsent', JSON.stringify(updatedConsent));
      if (typeof window.__applyConsent === 'function') {
        window.__applyConsent(updatedConsent);
      }
      setShowBanner(false);
      setShowSettings(false);
    }
  };

  const acceptAll = () => {
    const allConsent = {
      cookies_essential: true,
      cookies_analytics: true,
      cookies_marketing: true,
      email_marketing: true
    };
    setConsent(allConsent);
    saveConsent(allConsent);
  };

  const acceptEssential = () => {
    const essentialOnly = {
      cookies_essential: true,
      cookies_analytics: false,
      cookies_marketing: false,
      email_marketing: false
    };
    setConsent(essentialOnly);
    saveConsent(essentialOnly);
  };

  const saveCustomPreferences = () => {
    saveConsent(consent);
  };

  if (!showBanner && !showSettings) return null;

  return (
    <>
      {/* Main Banner */}
      {showBanner && !showSettings && (
        <div className="fixed bottom-3 left-3 right-3 sm:bottom-4 sm:left-auto sm:right-4 sm:max-w-md z-50 animate-in slide-in-from-bottom duration-300">
          <Card className="bg-white shadow-2xl border-slate-200 relative">
            <CardContent className="p-3.5">
              <div className="flex items-start gap-2.5">
                <Cookie className="h-4 w-4 text-violet-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 pr-4">
                  <h3 className="font-semibold text-slate-900 text-sm mb-0.5">We value your privacy</h3>
                  <p className="text-slate-600 text-xs leading-relaxed mb-2.5">
                    We use cookies to analyse traffic and personalise your experience.{' '}
                    <a href="/privacy" className="text-violet-600 hover:underline">Privacy</a>
                    {' · '}
                    <a href="/cookie-policy" className="text-violet-600 hover:underline">Cookies</a>
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      onClick={acceptAll}
                      className="bg-violet-600 hover:bg-violet-700 text-white h-7 px-2.5 text-xs"
                      data-testid="cookie-accept-all"
                    >
                      Accept all
                    </Button>
                    <Button
                      onClick={acceptEssential}
                      variant="outline"
                      className="border-slate-300 h-7 px-2.5 text-xs"
                      data-testid="cookie-essential-only"
                    >
                      Essential only
                    </Button>
                    <Button
                      onClick={() => setShowSettings(true)}
                      variant="ghost"
                      className="text-slate-600 h-7 px-2 text-xs"
                      data-testid="cookie-customize"
                    >
                      <Settings className="mr-1 h-3 w-3" />
                      Customise
                    </Button>
                  </div>
                </div>
                <button
                  onClick={() => setShowBanner(false)}
                  aria-label="Dismiss cookie banner"
                  className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-violet-600" />
              Cookie Preferences
            </DialogTitle>
            <DialogDescription>
              Manage your cookie preferences. Essential cookies cannot be disabled as they are required for the site to function.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Essential Cookies */}
            <div className="flex items-start justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div>
                  <Label className="font-semibold text-slate-900">Essential Cookies</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    Required for basic site functionality. Cannot be disabled.
                  </p>
                </div>
              </div>
              <Switch checked={true} disabled className="data-[state=checked]:bg-emerald-600" />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-start justify-between p-4 border border-slate-200 rounded-lg">
              <div className="flex items-start gap-3">
                <BarChart3 className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <Label className="font-semibold text-slate-900">Analytics Cookies</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    Help us understand how visitors interact with our website.
                  </p>
                </div>
              </div>
              <Switch 
                checked={consent.cookies_analytics}
                onCheckedChange={(checked) => setConsent({ ...consent, cookies_analytics: checked })}
                data-testid="cookie-analytics-toggle"
              />
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-start justify-between p-4 border border-slate-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Megaphone className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <Label className="font-semibold text-slate-900">Marketing Cookies</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    Used to deliver relevant advertisements and track campaign effectiveness.
                  </p>
                </div>
              </div>
              <Switch 
                checked={consent.cookies_marketing}
                onCheckedChange={(checked) => setConsent({ ...consent, cookies_marketing: checked })}
                data-testid="cookie-marketing-toggle"
              />
            </div>

            {/* Email Marketing */}
            <div className="flex items-start justify-between p-4 border border-slate-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Cookie className="h-5 w-5 text-violet-600 mt-0.5" />
                <div>
                  <Label className="font-semibold text-slate-900">Email Marketing</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    Receive promotional emails and product updates.
                  </p>
                </div>
              </div>
              <Switch 
                checked={consent.email_marketing}
                onCheckedChange={(checked) => setConsent({ ...consent, email_marketing: checked })}
                data-testid="cookie-email-toggle"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveCustomPreferences}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="cookie-save-preferences"
            >
              Save Preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
